type OmniboxTarget = { kind: "url"; url: string } | { kind: "search"; query: string };

const SCHEME_URL = /^https?:\/\/\S+$/i;
const LOCALHOST = /^localhost(:\d{1,5})?(\/\S*)?$/i;
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}(:\d{1,5})?(\/\S*)?$/;
const DOMAIN = /^([a-z0-9-]+\.)+[a-z]{2,}(:\d{1,5})?(\/\S*)?$/i;

/**
 * Decides whether what the user typed in the address row is a URL to open or
 * a query to search. Only http(s) URLs are opened, so `javascript:` and other
 * schemes fall through to search.
 */
function resolveOmniboxInput(input: string): OmniboxTarget | null {
  const text = input.trim();
  if (!text) return null;
  if (/\s/.test(text)) return { kind: "search", query: text };
  if (SCHEME_URL.test(text)) return { kind: "url", url: text };
  if (LOCALHOST.test(text) || IPV4.test(text)) return { kind: "url", url: `http://${text}` };
  if (DOMAIN.test(text)) return { kind: "url", url: `https://${text}` };
  return { kind: "search", query: text };
}

/** What the new tab bar does with what was typed: open it, search the web, or ask the AI. */
export type BarMode = "url" | "search" | "ask";

export type BarTarget =
  | { kind: "url"; url: string }
  | { kind: "search"; query: string }
  | { kind: "ask"; prompt: string };

// Words that open a question or an instruction to an assistant rather than a keyword search.
const ASK_OPENERS = new Set([
  "what",
  "what's",
  "whats",
  "why",
  "how",
  "how's",
  "who",
  "who's",
  "whom",
  "whose",
  "when",
  "where",
  "which",
  "is",
  "are",
  "was",
  "were",
  "can",
  "could",
  "should",
  "would",
  "will",
  "do",
  "does",
  "did",
  "explain",
  "tell",
  "write",
  "rewrite",
  "summarize",
  "summarise",
  "compare",
  "help",
  "give",
  "draft",
  "translate",
  "describe",
  "create",
  "generate",
  "plan",
  "suggest",
  "please",
]);

/** Phrases this long read as a prompt, not as search keywords. */
const ASK_MIN_WORDS = 7;

/** True when the text reads as a question or an instruction for the AI. */
function looksLikeQuestion(input: string): boolean {
  const text = input.trim();
  if (!text) return false;
  if (text.endsWith("?")) return true;
  const words = text.toLowerCase().split(/\s+/);
  const first = words[0]?.replace(/[^a-z']/g, "") ?? "";
  if (words.length > 1 && ASK_OPENERS.has(first)) return true;
  return words.length >= ASK_MIN_WORDS;
}

/**
 * The mode the bar picks on its own: a URL opens, a question asks, and any
 * other phrase searches. Without AI (signed out), questions search too.
 */
function detectBarMode(input: string, canAsk = true): BarMode | null {
  const target = resolveOmniboxInput(input);
  if (!target) return null;
  if (target.kind === "url") return "url";
  return canAsk && looksLikeQuestion(input) ? "ask" : "search";
}

/** The modes Tab cycles through for this text, the detected one first. */
export function availableBarModes(input: string, canAsk = true): BarMode[] {
  const detected = detectBarMode(input, canAsk);
  if (!detected) return [];
  const modes: BarMode[] = [];
  if (resolveOmniboxInput(input)?.kind === "url") modes.push("url");
  modes.push("search");
  if (canAsk) modes.push("ask");
  return [detected, ...modes.filter((m) => m !== detected)];
}

/** The mode after `current` in the Tab cycle, wrapping around. */
export function nextBarMode(current: BarMode, modes: BarMode[]): BarMode {
  if (modes.length === 0) return current;
  const index = modes.indexOf(current);
  return modes[(index + 1) % modes.length] ?? current;
}

/**
 * What submitting the bar does in a mode. `url` falls back to search when the
 * text is not a URL, so a stale mode can never open something unexpected.
 */
export function resolveBarInput(input: string, mode: BarMode): BarTarget | null {
  const text = input.trim();
  if (!text) return null;
  if (mode === "ask") return { kind: "ask", prompt: text };
  const target = resolveOmniboxInput(text);
  if (mode === "url" && target?.kind === "url") return target;
  return { kind: "search", query: text };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact, lowercase relative time for console-style metadata ("3m ago"). */
export function formatRelativeTime(timestamp: number, now: number = Date.now()): string {
  const diff = Math.max(0, now - timestamp);
  if (diff < MINUTE) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)}m ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)}h ago`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
